import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIdBadge,
  faUser,
  faEnvelope,
  faUserShield,
  faPause,
  faPlay,
  faBan,
  faCamera,
  faLock,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { InvisilatorService } from "Services/InvisilatorService";

interface Props {
  show: boolean;
  onClose: () => void;
  data: any;
  onUpdate: (updatedData: any) => void;
}

const PERMISSIONS_META = [
  { key: "canPauseExam", label: "Pause Exam", icon: faPause },
  { key: "canResumeExam", label: "Resume Exam", icon: faPlay },
  { key: "canTerminateExam", label: "Terminate Exam", icon: faBan },
  { key: "canForceCameraOn", label: "Force Camera On", icon: faCamera },
  { key: "canLockScreen", label: "Lock Screen", icon: faLock },
  { key: "canExtendTime", label: "Extend Time", icon: faClock },
];

export default function EditInvigilatorModal({
  show,
  onClose,
  data,
  onUpdate,
}: Props) {
  const [formData, setFormData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({ ...data });
    }
  }, [data]);

  if (!formData) return null;

  const togglePermission = (key: string) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: !formData.permissions[key],
      },
    });
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        email: formData.email,
        // role: formData.role?.toUpperCase().replace(" ", "_"),
        permissions: formData.permissions,
      };

      const res = await InvisilatorService.updateInvigilator(
        formData._id,
        payload,
      );

      if (res?.status === 200) {
        onUpdate(formData);
        onClose();
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Edit Invigilator</Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light">
        <div className="bg-white rounded p-4 mb-4 shadow-sm">
          <h6 className="fw-bold mb-3">
            <FontAwesomeIcon
              icon={faUserShield}
              className="me-2 text-primary"
            />
            Basic Information
          </h6>

          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faIdBadge} className="me-2" />
                Invigilator ID
              </Form.Label>
              <Form.Control value={formData.invigilatorId} disabled />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Name
              </Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Email
              </Form.Label>
              <Form.Control
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            {/* <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faUserShield} className="me-2" />
                Role
              </Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
              >
                <option>Invigilator</option>
                <option>Senior Invigilator</option>
              </Form.Select>
            </div> */}
          </div>
        </div>

        <div className="bg-white rounded p-4 shadow-sm">
          <h6 className="fw-bold mb-3">Permissions</h6>

          <div className="row">
            {PERMISSIONS_META.map((p) => (
              <div className="col-md-4 mb-3" key={p.key}>
                <div className="border rounded p-3 d-flex justify-content-between align-items-center">
                  <span>
                    <FontAwesomeIcon icon={p.icon} className="me-2" />
                    {p.label}
                  </span>
                  <Form.Check
                    type="switch"
                    checked={formData.permissions[p.key]}
                    onChange={() => togglePermission(p.key)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleUpdate} disabled={loading}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Update Invigilator"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
